import { useSnackbarStore } from '@components/shared/Snackbar/store';
import { useAppbarTitle } from '@hooks/useAppbarTitle';
import useLanguageNavigate from '@hooks/useLanguageNavigate';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import DateRangeOutlinedIcon from '@mui/icons-material/DateRangeOutlined';
import FmdGoodOutlinedIcon from '@mui/icons-material/FmdGoodOutlined';
import MapsHomeWorkOutlinedIcon from '@mui/icons-material/MapsHomeWorkOutlined';
import { Avatar, Box, Button, Chip, CircularProgress, Tooltip, Typography } from '@mui/material';
import { format, parseISO } from 'date-fns';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useUser } from 'services/users';
import { useSpecialistOffersStore } from '../services';
import {
	cancelOffer,
	createOffer,
	getOfferDetails,
	getRequestDetailById,
	updateOffer,
} from '../services/services';
import { CreateOfferRequest, OfferDetails, RequestType } from '../types';
// You would need to import or create these components
import { GeneralMuiModal } from '@components/shared/general-mui-modal';
import { TaMaps } from '@components/shared/Maps/TaMaps';
import { ActionButtons } from '@features/specialistOffer/components/ActionButtons';
import { Attachments } from '@features/specialistOffer/components/Attachments';
import { useModalDimensions } from '@hooks/useModalDimensions';
import { useTruncate } from '@hooks/useTruncate';
import { SendOfferData, SendOfferModal } from '../components/SendOfferModal';

// Mock imports (replace with actual imports as needed)
import speak from '@assets/icons/speak.svg';

const flagCDN = 'https://flagcdn.com/w160/';

export const OfferDetailPage: React.FC = () => {
	useAppbarTitle('offerDetails');

	const { t } = useTranslation();
	const { offerId } = useParams<{ offerId: string }>();
	const { navigateWithLanguage } = useLanguageNavigate();
	const { truncate, shouldTruncate } = useTruncate();
	const modalDimensions = useModalDimensions();
	const { data: user } = useUser();

	const {
		selectedOffer,
		isOfferDetailLoading,
		offerDetailError,
		setSelectedOffer,
		setIsOfferDetailLoading,
		setOfferDetailError,
	} = useSpecialistOffersStore();

	const [mapOpen, setMapOpen] = useState(false);
	const [isSending, setIsSending] = useState(false);
	const [isOfferModalOpen, setIsOfferModalOpen] = useState(false);
	const [offerDetails, setOfferDetails] = useState<OfferDetails | null>(null);
	const [isLoadingOfferDetails, setIsLoadingOfferDetails] = useState(false);
	const [forceCancelDialogClose, setForceCancelDialogClose] = useState(false);
	const showSnackbar = useSnackbarStore((state) => state.showSnackbar);

	// Fetch request details when component mounts
	useEffect(() => {
		const fetchOfferDetails = async () => {
			if (!offerId) return;

			try {
				setIsOfferDetailLoading(true);
				const offerDetails = await getRequestDetailById(offerId);
				
				// Add a debug log to see the payment_status
				console.log(`OfferDetailPage payment_status: ${offerDetails.payment_status}, is_paid: ${offerDetails.is_paid}`);
				console.log('End date from API:', offerDetails.end_date);
				
				setSelectedOffer(offerDetails);
				setIsOfferDetailLoading(false);

				// If there's an existing offer, fetch the offer details
				if (offerDetails.is_sent && offerDetails.offer_id) {
					try {
						setIsLoadingOfferDetails(true);
						const details = await getOfferDetails(offerDetails.offer_id);
						setOfferDetails(details);
						setIsLoadingOfferDetails(false);
					} catch (error) {
						showSnackbar(t('error'), t('failedToFetchOfferDetails'), 'error');
						setIsLoadingOfferDetails(false);
					}
				}
			} catch (error) {
				setIsOfferDetailLoading(false);
				setOfferDetailError(
					error instanceof Error ? error.message : 'Failed to fetch offer details'
				);
			}
		};

		fetchOfferDetails();

		// Cleanup function
		return () => {
			setSelectedOffer(null);
			setOfferDetails(null);
		};
	}, [offerId, setIsOfferDetailLoading, setSelectedOffer, setOfferDetailError, showSnackbar, t]);

	const handleGoBack = () => {
		navigateWithLanguage('/specialist/seasonal-onetime/offers');
	};

	const handleSendOfferClick = async () => {
		if (selectedOffer?.is_sent && selectedOffer?.offer_id) {
			// If we don't already have the offer details, fetch them
			if (!offerDetails) {
				try {
					setIsLoadingOfferDetails(true);
					const details = await getOfferDetails(selectedOffer.offer_id);
					setOfferDetails(details);
					setIsLoadingOfferDetails(false);
				} catch (error) {
					showSnackbar(t('error'), t('failedToFetchOfferDetails'), 'error');
					setIsLoadingOfferDetails(false);
					// Don't open the modal if we failed to fetch offer details for editing
					return;
				}
			}
		}
		setIsOfferModalOpen(true);
	};

	const handleCloseOfferModal = () => {
		setIsOfferModalOpen(false);
	};

	const handleEditOffer = async (offerData: SendOfferData) => {
		if (!selectedOffer?.id || !user?.id || !offerDetails) {
			showSnackbar(t('error'), t('missingRequiredInformationToSendOffer'), 'error');
			return;
		}

		try {
			setIsSending(true);

			const payload: CreateOfferRequest = {
				farmer_id: selectedOffer.farmer_id,
				specialist_id: user.user_id,
				request_id: offerDetails.request_id,
				price: offerData.isFree ? '0' : offerData.price.toString(),
				currency: offerData.currency,
				is_free: offerData.isFree || false,
				is_default_price: offerData.isFixedPrice || false,
				consultation_type: selectedOffer.request_type,
				offer_status: 'pending',
				offer_comment: offerData.message || '',
			};

			await updateOffer(offerDetails.id, payload);
			showSnackbar(t('success'), t('profileUpdated', { title: t('offer') }), 'success');

			// Refresh the offer details
			const updatedOfferDetails = await getRequestDetailById(selectedOffer.id);
			setSelectedOffer(updatedOfferDetails);

			// Also refresh the offer-specific details
			if (updatedOfferDetails.offer_id) {
				const updatedOffer = await getOfferDetails(updatedOfferDetails.offer_id);
				setOfferDetails(updatedOffer);
			}

			setIsOfferModalOpen(false);
		} catch (error) {
			showSnackbar(t('error'), t('somethingWentWrong'), 'error');
		} finally {
			setIsSending(false);
		}
	};

	const handleSendOffer = async (offerData: SendOfferData) => {
		if (!selectedOffer || !user?.id) {
			showSnackbar(t('error'), t('missingRequiredInformationToSendOffer'), 'error');
			return;
		}

		try {
			setIsSending(true);

			// For creating new offers (not editing), we use the current user's ID as the specialist
			const payload: CreateOfferRequest = {
				farmer_id: selectedOffer.farmer.id,
				specialist_id: user.id, // For new offers, we use the current user ID
				request_id: selectedOffer.id,
				price: offerData.isFree ? '0' : offerData.price.toString(),
				currency: offerData.currency,
				is_free: offerData.isFree || false,
				is_default_price: offerData.isFixedPrice || false,
				consultation_type: selectedOffer.request_type,
				offer_status: 'ongoing',
				offer_comment: offerData.message || '',
			};

			await createOffer(payload);
			showSnackbar(t('success'), t('offerSentSuccessfully'), 'success');

			// Refresh the offer details
			const updatedOfferDetails = await getRequestDetailById(selectedOffer.id);
			setSelectedOffer(updatedOfferDetails);

			// Also fetch the new offer details
			if (updatedOfferDetails.offer_id) {
				const newOfferDetails = await getOfferDetails(updatedOfferDetails.offer_id);
				setOfferDetails(newOfferDetails);
			}

			setIsOfferModalOpen(false);
		} catch (error) {
			showSnackbar(t('error'), t('somethingWentWrong'), 'error');
		} finally {
			setIsSending(false);
		}
	};

	const handleCancelOffer = async () => {
		if (!selectedOffer?.id || !offerDetails?.id) {
			showSnackbar(t('error'), t('noOfferToCancel'), 'error');
			return;
		}

		try {
			setIsSending(true);
			const cancelResponse = await cancelOffer(offerDetails.id);

			// Use the response to update the UI directly or fetch fresh data
			if (cancelResponse) {
				// Either update the selectedOffer state directly or fetch fresh data
				const updatedOfferDetails = await getRequestDetailById(selectedOffer.id);
				setSelectedOffer(updatedOfferDetails);
				setOfferDetails(null);
			}

			showSnackbar(t('success'), t('offerCanceledDescription'), 'success');

			// Close both the offer modal and the cancel confirmation dialog
			setForceCancelDialogClose(true);
			setIsOfferModalOpen(false);

			// Reset the forceCancelDialogClose flag after a short delay
			setTimeout(() => {
				setForceCancelDialogClose(false);
			}, 500);
		} catch (error) {
			showSnackbar(
				t('error'),
				error instanceof Error ? error.message : t('somethingWentWrong'),
				'error'
			);
		} finally {
			setIsSending(false);
		}
	};

	const handleNavigateToChat = () => {
		if (selectedOffer?.channelId) {
			navigateWithLanguage(`/specialist/chats?channelId=${selectedOffer.channelId}`);
		} else {
			console.log('No channel ID available for this offer');
		}
	};

	const openMapsModal = () => setMapOpen(true);
	const closeMapsModal = () => setMapOpen(false);

	// Calculate time remaining based on end_date
	const timeRemaining = useMemo(() => {
		if (selectedOffer?.end_date) {
			try {
				// For ISO format like "2025-07-25T13:15:08.000Z"
				const endDateObj = new Date(selectedOffer.end_date);
				
				if (endDateObj instanceof Date && !isNaN(endDateObj.getTime())) {
					const currentDate = new Date();
					console.log('End date from API (Detail):', endDateObj);
					console.log('Current date:', currentDate);
					
					// Calculate time difference
					const diffTime = endDateObj.getTime() - currentDate.getTime();
					console.log('Diff time (ms):', diffTime);
					
					// Convert to hours and days
					const diffHours = Math.ceil(diffTime / (1000 * 60 * 60));
					const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
					console.log('Hours remaining:', diffHours);
					console.log('Days remaining:', diffDays);
					
					// If time is in the future
					if (diffTime > 0) {
						// If less than 1 day remains, show hours
						if (diffTime < 24 * 60 * 60 * 1000) {
							console.log('Using hours:', diffHours);
							return { value: diffHours, unit: 'hours' };
						} else {
							console.log('Using days:', diffDays);
							return { value: diffDays, unit: 'days' };
						}
					} else {
						// If time has expired, show 0 days
						console.log('Time expired, showing 0 days');
						return { value: 0, unit: 'days' };
					}
				}
			} catch (error) {
				console.error('Error calculating time remaining:', error);
			}
		}
		
		// Fallback to request_valid_for_days or default value
		const days = selectedOffer?.request_valid_for_days ? parseInt(selectedOffer.request_valid_for_days) : 5;
		console.log('Using fallback days:', days);
		return { value: days, unit: 'days' };
	}, [selectedOffer?.end_date, selectedOffer?.request_valid_for_days]);

	if (isOfferDetailLoading) {
		return (
			<Box className="flex justify-center items-center h-96">
				<CircularProgress />
			</Box>
		);
	}

	if (offerDetailError || !selectedOffer) {
		return (
			<Box className="flex flex-col items-center justify-center h-96">
				<Typography color="error" className="mb-4">
					{offerDetailError || 'Offer not found'}
				</Typography>
				<Button variant="text" onClick={handleGoBack}>
					{t('back')}
				</Button>
			</Box>
		);
	}

	const {
		crop,
		farmer,
		request_type,
		start_date,
		end_date,
		sent_date,
		farm_details,
		communication_languages,
		description,
		attachments,
		is_paid,
		is_sent,
		payment_status,
		price,
		currency,
		duration_timestamp,
		duration_days,
	} = selectedOffer;

	const isSeasonalConsultation = request_type === RequestType.SEASONAL;
	const isPaid = is_paid || payment_status === 'paid' || payment_status === 'stripe_payed' || payment_status === 'Accepted';

	// Calculate status message based on payment status
	let statusMessage = '';
	if (isPaid) {
		// Paid status - "Chat closes in X days/hours"
		if (timeRemaining.unit === 'hours') {
			statusMessage = `Chat closes in ${timeRemaining.value} hours`;
		} else {
			statusMessage = `Chat closes in ${timeRemaining.value} days`;
		}
	} else {
		// Unpaid status - "Request active for X days"
		if (timeRemaining.value > 0) {
			statusMessage = `Request active for ${timeRemaining.value} days`;
		} else {
			// If days are negative or zero, show a generic message
			statusMessage = t('requestActive');
		}
	}

	return (
		<div className="flex flex-col gap-[20px] pb-5 max-md:px-5">
			{/* Back Button */}
			<div className="flex justify-between items-center mb-[15px]">
				<Button
					onClick={handleGoBack}
					variant="text"
					sx={{
						color: '#343A40',
						fontFamily: 'Roboto',
						textDecoration: 'underline',
						fontSize: '16px',
						fontStyle: 'normal',
						fontWeight: 700,
						lineHeight: 'normal',
						textTransform: 'capitalize',
						gap: '10px',
						padding: 0,
						'@media (max-width: 430px)': {
							gap: '5px',
							fontSize: '14px',
						},
					}}
				>
					<ArrowBackOutlinedIcon />
					{t('back')}
				</Button>
			</div>

			{/* Top Section with Crop, Farmer, and Consultation Info */}
			<div className="flex flex-col md:flex-row items-start md:items-center gap-0 md:gap-[35px]">
				{/* Crop Information */}
				<div className="flex items-center gap-[20px] border-b border-[#E9ECEF] md:border-none mb-[20px] md:mb-0 md:pb-0 pb-[20px] w-full md:w-auto">
					<Avatar
						src={crop.image_url}
						alt={crop.name}
						sx={{ width: 60, height: 60, borderRadius: '12px' }}
					/>
					<div className="flex flex-col gap-[10px]">
						{shouldTruncate(crop.name, 20) ? (
							<Tooltip title={crop.name}>
								<h4 className="text-dark font-roboto text-base font-bold">
									{truncate(crop.name, 20)}
								</h4>
							</Tooltip>
						) : (
							<h4 className="text-dark font-roboto text-base font-bold">{crop.name}</h4>
						)}
					</div>
				</div>

				{/* Vertical Divider */}
				<div className="h-[50px] border-r border-[#E9ECEF] hidden md:block"></div>

				{/* Farmer Info */}
				<div className="flex items-center gap-[20px] border-b border-[#E9ECEF] md:border-none mb-[20px] md:mb-0 md:pb-0 pb-[20px] w-full md:w-auto">
					<Avatar
						src={farmer.profile_image_url}
						alt={farmer.full_name}
						sx={{ width: 60, height: 60, borderRadius: '12px' }}
					/>
					<div className="flex flex-col gap-[5px]">
						{shouldTruncate(farmer.full_name, 20) ? (
							<Tooltip title={farmer.full_name}>
								<h4 className="text-dark font-roboto text-base font-bold">
									{truncate(farmer.full_name, 20)}
								</h4>
							</Tooltip>
						) : (
							<h4 className="text-dark font-roboto text-base font-bold">{farmer.full_name}</h4>
						)}
						{farmer.country && (
							<h6 className="text-secondary font-roboto text-base font-normal flex items-center gap-[5px]">
								<Avatar
									src={`${flagCDN}${farmer?.country?.alpha_two.toLowerCase()}.png`}
									alt={farmer?.country?.alpha_two}
									style={{ width: '20px', height: '16px', borderRadius: 0 }}
								/>
								{farmer.country.name}
							</h6>
						)}
					</div>

					{/* Price Box */}
					{is_sent && price ? (
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								ml: 'auto',
								width: '64px',
								height: '64px',
								backgroundColor: '#FFF9EB',
								borderRadius: '8px',
								border: '1px solid #FFDB8E',
							}}
						>
							<Typography
								variant="h4"
								sx={{
									fontWeight: 'bold',
									color: '#343a40',
								}}
							>
								€{price}
							</Typography>
						</Box>
					) : (
						<></>
					)}
				</div>

				{/* Vertical Divider */}
				<div className="h-[50px] border-r border-[#E9ECEF] hidden md:block"></div>

				<div className="flex flex-col items-center gap-2 border-b border-[#E9ECEF] md:border-none mb-[20px] md:mb-0 md:pb-0 pb-[20px] w-full md:w-auto">
					<span className="text-dark font-roboto text-base font-bold">
						{request_type === RequestType.SEASONAL
							? t('findSpecialistPage.seasonalConsultation')
							: t('ONE_TIME')}
					</span>
					{request_type === RequestType.ONE_TIME && (
						<span className="text-secondary font-roboto text-base font-normal">
							{t('oneTimeDuration', {
								duration: duration_days,
							})}
						</span>
					)}
				</div>

				{/* Vertical Divider */}
				<div className="h-[50px] border-r border-[#E9ECEF] hidden md:block"></div>

				<div className="flex items-center gap-[20px]">
					<div className="p-[10px] bg-disabled rounded-[8px]">
						<DateRangeOutlinedIcon sx={{ color: '#7B809A' }} />
					</div>
					<div className="flex flex-col">
						<h4 className="text-dark font-roboto text-base font-bold">
							{end_date
								? format(parseISO(end_date), 'dd.MM.yyyy')
								: isSeasonalConsultation && start_date
									? format(parseISO(start_date), 'dd.MM.yyyy')
									: sent_date
										? format(parseISO(sent_date), 'dd.MM.yyyy')
										: ''}
						</h4>
						<h6 className="text-secondary font-roboto text-base font-normal">
							{statusMessage}
						</h6>
					</div>
				</div>
			</div>

			{/* Horizontal Divider */}
			<div className="w-full h-[1px] bg-light"></div>

			{request_type === RequestType.SEASONAL && (
				<div className="flex flex-col border-b border-[#E9ECEF] pb-[20px] gap-[15px]">
					<h6 className="text-dark roboto-bold text-[16px] leading-normal">
						{t('consultationPeriod')}
					</h6>
					<div className="flex gap-[15px]">
						<div className="flex gap-[15px] flex-col md:flex-row">
							<div className="flex items-center gap-[15px]">
								<CalendarMonthIcon sx={{ color: '#7B809A' }} />
								<p className="text-secondary font-roboto text-sm font-normal">
									{moment(start_date).format('DD.MM.YYYY')} -{' '}
									{moment(end_date).format('DD.MM.YYYY')}
								</p>
							</div>
						</div>
					</div>
				</div>
			)}

			{/* Farm Details Section */}
			<div className="flex flex-col border-b border-[#E9ECEF] pb-[20px] gap-[15px]">
				<h6 className="text-dark roboto-bold text-[16px] leading-normal">{t('farmDetails')}</h6>
				<div className="flex gap-[15px]">
					<div className="flex gap-[15px] flex-col md:flex-row">
						<div className="flex items-center gap-[15px]">
							<MapsHomeWorkOutlinedIcon sx={{ color: '#7B809A' }} />
							<p className="text-secondary font-roboto text-sm font-normal">
								{farm_details.area} {farm_details.unit}
							</p>
						</div>
						<div className="flex items-center gap-[15px]">
							<FmdGoodOutlinedIcon sx={{ color: '#7B809A' }} />
							<div className="flex items-center">
								<p className="text-secondary font-roboto text-sm font-normal">
									{truncate(farm_details.location.address, 50)}
									<span
										className="text-primary font-roboto text-sm font-bold text-right underline decoration-solid pl-[10px] cursor-pointer"
										onClick={openMapsModal}
									>
										{t('viewOnMap')}
									</span>
								</p>
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* Communication Languages Section */}
			<div className="flex flex-col gap-[10px]">
				<h6 className="text-dark roboto-bold text-[16px] leading-normal">
					{t('communicationLanguage')}
				</h6>
				<div className="flex gap-[10px] items-center">
					<img className="text-secondary" src={speak} alt="speak" />
					<div className="flex gap-[10px] items-center">
						{communication_languages.map((language) => (
							<Chip
								key={language.id}
								label={language.name}
								variant="outlined"
								sx={{
									width: 'max-content',
									borderRadius: '8px',
									border: '1px solid #F8F9FA',
									backgroundColor: '#F8F9FA',
									padding: '1px 10px',
									color: '#7B809A',
									fontFamily: 'Roboto',
									fontSize: '16px',
									fontStyle: 'normal',
									fontWeight: 400,
									lineHeight: '140%',
									height: '24px',
									textTransform: 'camelcase',
									'& .MuiChip-label': {
										padding: 0,
									},
								}}
							/>
						))}
					</div>
				</div>
			</div>

			{/* Horizontal Divider */}
			<div className="w-full h-[1px] bg-light"></div>

			{/* Description Section */}
			<div className="flex flex-col gap-[10px]">
				<h6 className="text-dark roboto-bold text-[16px] leading-normal">{t('info')}</h6>
				<div
					className="text-secondary roboto-regular text-[16px] leading-normal flex flex-col gap-[10px] w-full"
					dangerouslySetInnerHTML={{ __html: description || '' }}
				></div>
			</div>

			{/* Horizontal Divider */}
			<div className="w-full h-[1px] bg-light"></div>

			{/* Attachments Section */}
			{attachments && attachments.length > 0 && <Attachments files={attachments} />}

			{/* Action Buttons */}
			<ActionButtons
				onBack={handleGoBack}
				onChat={handleNavigateToChat}
				onSendOffer={handleSendOfferClick}
				hasOffer={is_sent}
				requestType={request_type}
				hideActions={false}
				isPaid={is_paid || payment_status === 'paid' || payment_status === 'stripe_payed' || payment_status === 'Accepted'}
				paymentStatus={payment_status}
			/>

			{/* Maps Modal */}
			<GeneralMuiModal
				maxWidth="xl"
				title={t('myFarm.farmLocation')}
				styles={{
					paperStyle: { width: modalDimensions.width, height: modalDimensions.height, padding: 0 },
					contentStyle: { padding: 0 },
				}}
				open={mapOpen}
				onClose={closeMapsModal}
				content={
					<TaMaps
						disabled={true}
						marker={{
							lng: Number(farm_details.location.longitude),
							lat: Number(farm_details.location.latitude),
						}}
						currentAddress={farm_details.location.address}
					/>
				}
			/>

			{/* Send Offer Modal */}
			<SendOfferModal
				isOpen={isOfferModalOpen}
				onClose={handleCloseOfferModal}
				onSendOffer={handleSendOffer}
				onEditOffer={handleEditOffer}
				onCancelOffer={handleCancelOffer}
				onChat={handleNavigateToChat}
				currency={offerDetails?.currency || currency || 'EUR'}
				initialPrice={offerDetails ? parseFloat(offerDetails.price) : price || 1}
				isEdit={is_sent}
				requestType={request_type}
				isSending={isSending || isLoadingOfferDetails}
				offerDetails={offerDetails}
				forceCancelDialogClose={forceCancelDialogClose}
			/>
		</div>
	);
};
